import { useState, type FormEvent } from 'react';
import type {
  EmployeeAchievementEntry,
  EmployeeExperienceEntry,
  EmployeeQualificationEntry,
  ExperienceAreaLibraryResponse,
  QualificationLibraryResponse,
  SkillLibraryResponse,
} from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import factStyles from './CaptureSection.module.css';
import formStyles from './AssessmentForm.module.css';

/**
 * Employee Fact Write Paths (Mission 162) — the "Add"/"Edit" controls for
 * Skills, Qualifications, Experience and Achievements, the four sections
 * `EmployeeProfilePage.tsx` renders as read-only `FactSection`s above.
 *
 * ---------------------------------------------------------------------
 * A FACT REMAINS A FACT
 *
 * Nothing below records a rating, a confidence level or a source type —
 * these forms send exactly the fields each table actually has (a
 * proficiency word, a credential's issuing details, a recorded duration, a
 * title). Recording a skill/qualification/experience/achievement is
 * authorized the same way attaching evidence to an existing skill already
 * is (`authorizeAssessmentWriteAccess` — self, direct manager, or an
 * eligible peer/direct report) — the same kind of act on the same kind of
 * entity, reusing the gate rather than inventing a narrower or wider one.
 *
 * `employee_skill` is append-only (docs/DATABASE.md §3) — there is
 * deliberately no `EditSkillControl`: a changed proficiency is recorded as
 * a new entry, which is exactly what `RecordSkillControl` already does.
 * Qualification, Experience and Achievement are ordinary correctable facts,
 * so each gets both a Record and an Edit control below.
 * ---------------------------------------------------------------------
 */

type SubmitState = { status: 'editing' } | { status: 'submitting' } | { status: 'failed'; message: string };

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RecordSkillControl({ employeeId, onRecorded }: { employeeId: string; onRecorded: () => void }) {
  const [open, setOpen] = useState(false);
  return open ? (
    <RecordSkillForm employeeId={employeeId} onRecorded={() => { setOpen(false); onRecorded(); }} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className={factStyles.addToggle} onClick={() => setOpen(true)}>
      Record a skill
    </button>
  );
}

function RecordSkillForm({ employeeId, onRecorded, onClose }: { employeeId: string; onRecorded: () => void; onClose: () => void }) {
  const library = useApiResource<SkillLibraryResponse>('/skills');
  const [skillDefinitionId, setSkillDefinitionId] = useState('');
  const [proficiencyLevel, setProficiencyLevel] = useState<'beginner' | 'intermediate' | 'advanced' | 'expert'>('beginner');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';

  if (library.status === 'loading') return <p className={formStyles.note}>Loading the skill library…</p>;
  if (library.status === 'failed') {
    return <p className={formStyles.error}>The skill library could not be loaded: {describeError(library.error)}</p>;
  }
  const active = library.data.skills.filter((s) => s.status === 'active');
  const selectedId = skillDefinitionId || active[0]?.id || '';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !selectedId) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/skills`, {
        skillDefinitionId: selectedId,
        proficiencyLevel,
        yearsOfExperience: yearsOfExperience.trim() ? Number(yearsOfExperience) : undefined,
      });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  if (active.length === 0) {
    return <p className={formStyles.note}>No active skills are defined in the organisation's library yet.</p>;
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Record a skill">
      <div className={formStyles.field}>
        <label htmlFor="skill-definition" className={formStyles.label}>
          Skill
        </label>
        <select id="skill-definition" className={factStyles.select} value={selectedId} onChange={(e) => setSkillDefinitionId(e.target.value)} disabled={submitting}>
          {active.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className={formStyles.field}>
        <label htmlFor="skill-proficiency" className={formStyles.label}>
          Proficiency
        </label>
        <select
          id="skill-proficiency"
          className={factStyles.select}
          value={proficiencyLevel}
          onChange={(e) => setProficiencyLevel(e.target.value as typeof proficiencyLevel)}
          disabled={submitting}
        >
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
          <option value="expert">Expert</option>
        </select>
      </div>
      <div className={formStyles.field}>
        <label htmlFor="skill-years" className={formStyles.label}>
          Years (optional)
        </label>
        <input
          id="skill-years"
          className={formStyles.text}
          type="number"
          min="0"
          step="0.5"
          value={yearsOfExperience}
          onChange={(e) => setYearsOfExperience(e.target.value)}
          disabled={submitting}
        />
      </div>
      {state.status === 'failed' ? <p className={formStyles.error} role="alert">{state.message}</p> : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Recording…' : 'Record'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function RecordQualificationControl({ employeeId, onRecorded }: { employeeId: string; onRecorded: () => void }) {
  const [open, setOpen] = useState(false);
  return open ? (
    <QualificationForm employeeId={employeeId} onDone={() => { setOpen(false); onRecorded(); }} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className={factStyles.addToggle} onClick={() => setOpen(true)}>
      Record a qualification
    </button>
  );
}

export function EditQualificationControl({
  employeeId,
  qualification,
  onChanged,
}: {
  employeeId: string;
  qualification: EmployeeQualificationEntry;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  return open ? (
    <QualificationForm
      employeeId={employeeId}
      editing={qualification}
      onDone={() => { setOpen(false); onChanged(); }}
      onClose={() => setOpen(false)}
    />
  ) : (
    <button type="button" className={factStyles.editToggle} onClick={() => setOpen(true)}>
      Edit
    </button>
  );
}

function QualificationForm({
  employeeId,
  editing,
  onDone,
  onClose,
}: {
  employeeId: string;
  editing?: EmployeeQualificationEntry;
  onDone: () => void;
  onClose: () => void;
}) {
  const library = useApiResource<QualificationLibraryResponse>(editing ? null : '/qualifications');
  const [qualificationDefinitionId, setQualificationDefinitionId] = useState('');
  const [issuingBody, setIssuingBody] = useState(editing?.issuingBody ?? '');
  const [obtainedOn, setObtainedOn] = useState(editing?.obtainedOn ?? '');
  const [expiresOn, setExpiresOn] = useState(editing?.expiresOn ?? '');
  const [verificationStatus, setVerificationStatus] = useState<'unverified' | 'pending' | 'verified'>(
    editing?.verificationStatus ?? 'unverified'
  );
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';

  const active = !editing && library.status === 'loaded' ? library.data.qualifications.filter((q) => q.status === 'active') : [];
  const selectedId = qualificationDefinitionId || active[0]?.id || '';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    if (!editing && !selectedId) return;
    setState({ status: 'submitting' });
    try {
      const body = {
        issuingBody: issuingBody.trim() || undefined,
        obtainedOn: obtainedOn || undefined,
        expiresOn: expiresOn || undefined,
        verificationStatus,
      };
      if (editing) {
        await apiPatch(`/employees/${employeeId}/qualifications/${editing.id}`, body);
      } else {
        await apiPost(`/employees/${employeeId}/qualifications`, { ...body, qualificationDefinitionId: selectedId });
      }
      onDone();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  if (!editing) {
    if (library.status === 'loading') return <p className={formStyles.note}>Loading the qualification library…</p>;
    if (library.status === 'failed') {
      return <p className={formStyles.error}>The qualification library could not be loaded: {describeError(library.error)}</p>;
    }
    if (active.length === 0) {
      return <p className={formStyles.note}>No active qualifications are defined in the organisation's library yet.</p>;
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={editing ? 'Edit qualification' : 'Record a qualification'}>
      {!editing ? (
        <div className={formStyles.field}>
          <label htmlFor="qualification-definition" className={formStyles.label}>
            Qualification
          </label>
          <select
            id="qualification-definition"
            className={factStyles.select}
            value={selectedId}
            onChange={(e) => setQualificationDefinitionId(e.target.value)}
            disabled={submitting}
          >
            {active.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className={formStyles.field}>
        <label htmlFor="qualification-issuer" className={formStyles.label}>
          Issued by (optional)
        </label>
        <input id="qualification-issuer" className={formStyles.text} value={issuingBody} onChange={(e) => setIssuingBody(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="qualification-obtained" className={formStyles.label}>
          Obtained on (optional, YYYY-MM-DD)
        </label>
        <input id="qualification-obtained" className={formStyles.text} value={obtainedOn ?? ''} onChange={(e) => setObtainedOn(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="qualification-expires" className={formStyles.label}>
          Expires on (optional, YYYY-MM-DD)
        </label>
        <input id="qualification-expires" className={formStyles.text} value={expiresOn ?? ''} onChange={(e) => setExpiresOn(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="qualification-verification" className={formStyles.label}>
          Verification
        </label>
        <select
          id="qualification-verification"
          className={factStyles.select}
          value={verificationStatus}
          onChange={(e) => setVerificationStatus(e.target.value as typeof verificationStatus)}
          disabled={submitting}
        >
          <option value="unverified">Unverified</option>
          <option value="pending">Pending</option>
          <option value="verified">Verified</option>
        </select>
      </div>
      {state.status === 'failed' ? <p className={formStyles.error} role="alert">{state.message}</p> : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Saving…' : editing ? 'Save' : 'Record'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function RecordExperienceControl({ employeeId, onRecorded }: { employeeId: string; onRecorded: () => void }) {
  const [open, setOpen] = useState(false);
  return open ? (
    <ExperienceForm employeeId={employeeId} onDone={() => { setOpen(false); onRecorded(); }} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className={factStyles.addToggle} onClick={() => setOpen(true)}>
      Record experience
    </button>
  );
}

export function EditExperienceControl({
  employeeId,
  experience,
  onChanged,
}: {
  employeeId: string;
  experience: EmployeeExperienceEntry;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  return open ? (
    <ExperienceForm employeeId={employeeId} editing={experience} onDone={() => { setOpen(false); onChanged(); }} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className={factStyles.editToggle} onClick={() => setOpen(true)}>
      Edit
    </button>
  );
}

function ExperienceForm({
  employeeId,
  editing,
  onDone,
  onClose,
}: {
  employeeId: string;
  editing?: EmployeeExperienceEntry;
  onDone: () => void;
  onClose: () => void;
}) {
  const library = useApiResource<ExperienceAreaLibraryResponse>(editing ? null : '/experience-areas');
  const [experienceAreaDefinitionId, setExperienceAreaDefinitionId] = useState('');
  const [startedOn, setStartedOn] = useState(editing?.startedOn ?? '');
  const [endedOn, setEndedOn] = useState(editing?.endedOn ?? '');
  const [durationYears, setDurationYears] = useState(editing?.durationYears != null ? String(editing.durationYears) : '');
  const [contextNotes, setContextNotes] = useState(editing?.contextNotes ?? '');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';

  const active = !editing && library.status === 'loaded' ? library.data.experienceAreas.filter((a) => a.status === 'active') : [];
  const selectedId = experienceAreaDefinitionId || active[0]?.id || '';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    if (!editing && !selectedId) return;
    setState({ status: 'submitting' });
    try {
      const body = {
        startedOn: startedOn || undefined,
        endedOn: endedOn || undefined,
        durationYears: durationYears.trim() ? Number(durationYears) : undefined,
        contextNotes: contextNotes.trim() || undefined,
      };
      if (editing) {
        await apiPatch(`/employees/${employeeId}/experience/${editing.id}`, body);
      } else {
        await apiPost(`/employees/${employeeId}/experience`, { ...body, experienceAreaDefinitionId: selectedId });
      }
      onDone();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  if (!editing) {
    if (library.status === 'loading') return <p className={formStyles.note}>Loading the experience-area library…</p>;
    if (library.status === 'failed') {
      return <p className={formStyles.error}>The experience-area library could not be loaded: {describeError(library.error)}</p>;
    }
    if (active.length === 0) {
      return <p className={formStyles.note}>No active experience areas are defined in the organisation's library yet.</p>;
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={editing ? 'Edit experience' : 'Record experience'}>
      {!editing ? (
        <div className={formStyles.field}>
          <label htmlFor="experience-area" className={formStyles.label}>
            Experience area
          </label>
          <select id="experience-area" className={factStyles.select} value={selectedId} onChange={(e) => setExperienceAreaDefinitionId(e.target.value)} disabled={submitting}>
            {active.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className={formStyles.field}>
        <label htmlFor="experience-started" className={formStyles.label}>
          Started on (optional, YYYY-MM-DD)
        </label>
        <input id="experience-started" className={formStyles.text} value={startedOn ?? ''} onChange={(e) => setStartedOn(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="experience-ended" className={formStyles.label}>
          Ended on (optional, YYYY-MM-DD)
        </label>
        <input id="experience-ended" className={formStyles.text} value={endedOn ?? ''} onChange={(e) => setEndedOn(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="experience-duration" className={formStyles.label}>
          Recorded duration in years (optional)
        </label>
        <input
          id="experience-duration"
          className={formStyles.text}
          type="number"
          min="0"
          step="0.5"
          value={durationYears}
          onChange={(e) => setDurationYears(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="experience-notes" className={formStyles.label}>
          Notes (optional)
        </label>
        <textarea id="experience-notes" className={formStyles.textarea} rows={2} value={contextNotes ?? ''} onChange={(e) => setContextNotes(e.target.value)} disabled={submitting} />
      </div>
      {state.status === 'failed' ? <p className={formStyles.error} role="alert">{state.message}</p> : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Saving…' : editing ? 'Save' : 'Record'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function RecordAchievementControl({ employeeId, onRecorded }: { employeeId: string; onRecorded: () => void }) {
  const [open, setOpen] = useState(false);
  return open ? (
    <AchievementForm employeeId={employeeId} onDone={() => { setOpen(false); onRecorded(); }} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className={factStyles.addToggle} onClick={() => setOpen(true)}>
      Record an achievement
    </button>
  );
}

export function EditAchievementControl({
  employeeId,
  achievement,
  onChanged,
}: {
  employeeId: string;
  achievement: EmployeeAchievementEntry;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  return open ? (
    <AchievementForm employeeId={employeeId} editing={achievement} onDone={() => { setOpen(false); onChanged(); }} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className={factStyles.editToggle} onClick={() => setOpen(true)}>
      Edit
    </button>
  );
}

function AchievementForm({
  employeeId,
  editing,
  onDone,
  onClose,
}: {
  employeeId: string;
  editing?: EmployeeAchievementEntry;
  onDone: () => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(editing?.title ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [occurredOn, setOccurredOn] = useState(editing?.occurredOn ?? todayIso());
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const titleIsValid = title.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !titleIsValid) return;
    setState({ status: 'submitting' });
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        occurredOn: occurredOn || undefined,
        relatedRoleId: editing?.relatedRole?.id,
        relatedTeamId: editing?.relatedTeam?.id,
      };
      if (editing) {
        await apiPatch(`/employees/${employeeId}/achievements/${editing.id}`, body);
      } else {
        await apiPost(`/employees/${employeeId}/achievements`, body);
      }
      onDone();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={editing ? 'Edit achievement' : 'Record an achievement'}>
      <div className={formStyles.field}>
        <label htmlFor="achievement-title" className={formStyles.label}>
          Title
        </label>
        <input id="achievement-title" className={formStyles.text} value={title} onChange={(e) => setTitle(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="achievement-description" className={formStyles.label}>
          Description (optional)
        </label>
        <textarea id="achievement-description" className={formStyles.textarea} rows={2} value={description ?? ''} onChange={(e) => setDescription(e.target.value)} disabled={submitting} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="achievement-occurred" className={formStyles.label}>
          Occurred on (optional, YYYY-MM-DD)
        </label>
        <input id="achievement-occurred" className={formStyles.text} value={occurredOn ?? ''} onChange={(e) => setOccurredOn(e.target.value)} disabled={submitting} />
      </div>
      {state.status === 'failed' ? <p className={formStyles.error} role="alert">{state.message}</p> : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !titleIsValid}>
          {submitting ? 'Saving…' : editing ? 'Save' : 'Record'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
